import { useApolloClient, useLazyQuery } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Button } from "@chakra-ui/react"
import * as yup from "yup";
import useEditAvatar from "../../hooks/useEditAvatar";
import useNotification from "../../hooks/useNotification";
import useTranslations from "../../hooks/useTranslations";
import useUpdateViewer from "../../hooks/useUpdateViewer";
import useConfirm from "../utils/confirm/useConfirm";
import { Button as WombatButton, Input, Radio, RadioGroup } from "../wombat-ui";
import { H3 } from "../wombat-ui/components/Typography";
import UserAvatar from "../utils/avatars/UserAvatar";
import { GetViewerDocument, useDeleteAvatar, useDeleteCredential } from '../../graphql/generated/definitions';

type ViewerInput = {
  id?: string;
  name: string;
  email: string;
  locale: string;
  avatar: {
    filename: string;
    data: string;
  };
};

type UserFormProps = {
  defaultValues?: ViewerInput;
};

const LOCALE_OPTIONS = [
  { value: "fr-FR", label: "Francais" },
  { value: "en-US", label: "English" },
];

const UserForm: React.FC<UserFormProps> = ({ defaultValues = {} }) => {
  const fileRef = useRef<{ filename: string; data: string }>(null);
  const t = useTranslations();
  const confirm = useConfirm();

  const [updateViewer] = useUpdateViewer();
  const { notifySuccess, notifyError } = useNotification();

  const ViewerFormSchema = useMemo(
    () =>
      yup.object().shape({
        name: yup.string().required(t("forms.errors.required") as string),
        email: yup
          .string()
          .email(t("forms.errors.email") as string)
          .required(t("forms.errors.required") as string),
      }),
    [t]
  );

  const { register, setValue, handleSubmit, errors, control } =
    useForm<ViewerInput>({
      resolver: yupResolver(ViewerFormSchema),
      defaultValues: {
        name: "",
        email: "",
        avatar: { filename: "", data: "" },
        ...defaultValues,
      },
    });

  const [deleteCredential] = useDeleteCredential();
  const apolloClient = useApolloClient();
  const handleLogout = useCallback(() => {
    confirm({
      title: t("header.logout"),
      message: t("viewerProfile.logoutConfirmation"),
      buttons: [
        { text: t("forms.actions.cancel") },
        {
          colorScheme: "red",
          id: "logout",
          text: t("header.logout"),
          onClick: async () => {
            await deleteCredential();
            apolloClient.resetStore();
          },
        },
      ],
    });
  }, [deleteCredential, apolloClient]);

  const [getViewer, { error: getViewerError }] = useLazyQuery(GetViewerDocument, {
    fetchPolicy: "network-only",
  });
  useEffect(() => {
    if (getViewerError) {
      deleteCredential();
      apolloClient.resetStore();
    }
  }, [getViewerError, deleteCredential, apolloClient]);

  const [ deleteAvatar ] = useDeleteAvatar();

  const onSubmit = useCallback(
    async ({ name, email, avatar, locale }) => {
      try {
        if (avatar.data === "") {
          await deleteAvatar({
            variables: {
              input: {
                userId: defaultValues.id
              }
            }
          });  
        }

        await updateViewer({
          variables: {
            input: {
              name,
              email,
              locale,
              avatar: avatar.filename !== "" ? avatar.data : null,
            },
          },
        });

        notifySuccess({
          title: t(`userProfile.notification.success`) as string,
          description: t(`userProfile.notification.profileUpdated`) as string,
        });
      } catch (error) {
        notifyError({
          title: t(`userProfile.notification.error`) as string,
        });
      }

      // We manually refetch the user instead of using refetchQueries so
      // we can easily know if the user was logged out. It can happen
      // when the password or email change.
      await getViewer();
    },
    [defaultValues, handleLogout]
  );

  useEffect(() => {
    register(`avatar.filename`);
    register(`avatar.data`);
  }, [errors]);

  const avatarData = useWatch({
    control,
    name: `avatar.data`,
    defaultValue: defaultValues?.avatar?.data
  });

  const editAvatar = useEditAvatar();
  const handleAvatarChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target?.files?.[0];

      editAvatar({
        title: t("users.avatar") as string,
        file,
        onComplete: (result) => {
          setValue(`avatar.filename`, file.name);
          setValue(`avatar.data`, result);
          fileRef.current = {
            filename: file.name,
            data: result as string,
          };
        }
      })
    },
    [editAvatar]
  );

  const handleDeleteAvatar = useCallback(() => {
    setValue(`avatar.filename`, "");
    setValue(`avatar.data`, "");
  }, []);

  return (
    <form
      className="relative block"
      id="userForm"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="flex flex-col">
        <div>
          <H3>{t("viewerProfile.myInformation")}</H3>
          <div>
            <label className="block pt-4 text-sm font-medium text-dark-100">
              {t("viewerProfile.avatar")}
            </label>
            <div className="flex items-center mt-2">
              <UserAvatar
                size={12}
                src={avatarData}
                alt="User-avatar"
              />
              <span className="relative ml-5 overflow-hidden rounded-md shadow-sm">
                <Button
                  type="button"
                  size="xs"
                >
                  {t("forms.actions.change")}
                </Button>
                <input
                  className="absolute top-0 right-0 block py-2 opacity-0 cursor-pointer"
                  type="file"
                  onChange={handleAvatarChange}
                  accept="image/png, image/jpeg, image/jpg"
                  name="avatar"
                />
              </span>
              {
                avatarData && (
                  <Button
                    type="button"
                    onClick={handleDeleteAvatar}
                    colorScheme="red"
                    size="xs"
                    ml={3}
                    variant="outline"
                    data-test-id="deleteAvatarButton"
                  >
                    {t("forms.actions.delete")}
                  </Button>
                )
              }
            </div>
          </div>
          <div className="grid grid-cols-1 col-span-4 mt-6 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <Input
                id="user-name"
                label={t("viewerProfile.name") as string}
                error={errors?.name}
                name={`name`}
                ref={register({ required: true })}
              />
            </div>
            <div className="sm:col-span-4">
              <Input
                id="user-email"
                label={t("viewerProfile.email") as string}
                error={errors?.email}
                name={`email`}
                type="email"
                ref={register({ required: true })}
              />
            </div>
          </div>
        </div>
        <H3>{t("viewerProfile.locale")}</H3>
        <RadioGroup>
          {LOCALE_OPTIONS.map(({ label, value }) => (
            <Radio
              key={value}
              id={`locale-${value}`}
              label={label}
              name="locale"
              value={value}
              ref={register}
            />
          ))}
        </RadioGroup>
        <H3>{t("viewerProfile.session")}</H3>
        <div className="pt-4">
          <WombatButton onClick={handleLogout} variant="danger" id="logoutButton">
            {t("viewerProfile.logout")}
          </WombatButton>
        </div>
      </div>
    </form>
  );
};

export default React.memo(UserForm);
